import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Plane, Check, X, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;
const isoToday = () => new Date().toISOString().slice(0, 10);
const inputCls = 'w-full px-3 py-2 rounded-md border text-sm bg-[#0F1923] border-[#2D3748] text-gray-100';
const STATUS_COLORS = { pending: '#FFA000', approved: '#00C896', denied: '#EF4444', cancelled: '#94A3B8' };
const TYPE_COLORS = { vacation: '#00C896', sick: '#FFA000', unpaid: '#94A3B8' };

function PtoCalendar({ requests }) {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const approved = requests.filter((r) => r.status === 'approved');
  const y = month.getFullYear();
  const m = month.getMonth();
  const startOffset = (new Date(y, m, 1).getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const iso = (d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const byDate = {};
  approved.forEach((r) => {
    for (let d = 1; d <= daysInMonth; d++) {
      const ds = iso(d);
      if (r.start_date <= ds && ds <= r.end_date) (byDate[ds] = byDate[ds] || []).push(r);
    }
  });
  const now = new Date();
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const cells = [...Array(startOffset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const label = month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  return (
    <div className="ht-card p-5 rounded-xl border border-[#2D3748] bg-[#111C2B]" data-testid="pto-calendar">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">Company time-off calendar</p>
        <div className="flex items-center gap-1">
          <button className="p-1 text-slate-400 hover:text-white" onClick={() => setMonth(new Date(y, m - 1, 1))} data-testid="pto-cal-prev"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-semibold text-white min-w-[130px] text-center" data-testid="pto-cal-label">{label}</span>
          <button className="p-1 text-slate-400 hover:text-white" onClick={() => setMonth(new Date(y, m + 1, 1))} data-testid="pto-cal-next"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center mb-1">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <span key={d} className="text-[10px] font-bold text-slate-500">{d}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={`x${i}`} />;
          const ds = iso(d);
          const offs = byDate[ds] || [];
          return (
            <div key={ds} className={`rounded-md border min-h-[56px] p-1 ${ds === todayIso ? 'border-[#1E88E5]/60 bg-blue-950/20' : 'border-[#1F2D3F]'}`} data-testid={`pto-cal-day-${ds}`}>
              <p className={`text-[10px] font-bold ${ds === todayIso ? 'text-[#1E88E5]' : 'text-slate-500'}`}>{d}</p>
              {offs.slice(0, 3).map((r, j) => (
                <p key={j} className="text-[9px] leading-tight truncate rounded px-1 mt-0.5"
                  style={{ color: TYPE_COLORS[r.type] || '#94A3B8', background: `${TYPE_COLORS[r.type] || '#94A3B8'}18` }}
                  title={`${r.user_name} — ${r.type} (${r.start_date} → ${r.end_date})`}>
                  {r.user_name}
                </p>
              ))}
              {offs.length > 3 && <p className="text-[9px] text-slate-500 px-1">+{offs.length - 3}</p>}
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-slate-500 mt-2">
        <span style={{ color: TYPE_COLORS.vacation }}>■</span> Vacation&nbsp;&nbsp;
        <span style={{ color: TYPE_COLORS.sick }}>■</span> Sick&nbsp;&nbsp;
        <span style={{ color: TYPE_COLORS.unpaid }}>■</span> Unpaid — approved requests only
      </p>
    </div>
  );
}

export default function TimeOff() {
  const { token, user, isAdmin, isSupervisor } = useAuth();
  const { toast } = useToast();
  const manager = isAdmin || isSupervisor;
  const headers = { Authorization: `Bearer ${token}` };
  const [balances, setBalances] = useState([]);
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ type: 'vacation', start_date: isoToday(), end_date: isoToday(), total_hours: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [editBal, setEditBal] = useState(null); // {user_id, vacation_hours, sick_hours}

  const load = useCallback(async () => {
    try {
      const [b, r] = await Promise.all([
        axios.get(`${API}/pto/balances`, { headers }),
        axios.get(`${API}/pto/requests`, { headers }),
      ]);
      setBalances(b.data.balances || []);
      setRequests(r.data.requests || []);
    } catch { }
    // eslint-disable-next-line
  }, [token]);
  useEffect(() => { if (token) load(); }, [token, load]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form, total_hours: form.total_hours ? parseFloat(form.total_hours) : null };
      await axios.post(`${API}/pto/requests`, payload, { headers });
      toast({ title: 'Request submitted', description: 'Admins have been notified.' });
      setForm({ type: 'vacation', start_date: isoToday(), end_date: isoToday(), total_hours: '', notes: '' });
      load();
    } catch (e) {
      toast({ title: 'Could not submit', description: e.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const decide = async (id, action) => {
    try {
      await axios.put(`${API}/pto/requests/${id}`, { action }, { headers });
      toast({ title: action === 'approve' ? 'Request approved' : action === 'deny' ? 'Request denied' : 'Cancelled — balance restored' });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    }
  };

  const cancel = async (id) => {
    try { await axios.delete(`${API}/pto/requests/${id}`, { headers }); toast({ title: 'Request cancelled' }); load(); }
    catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || '', variant: 'destructive' }); }
  };

  const saveBal = async () => {
    try {
      await axios.put(`${API}/pto/balances/${editBal.user_id}`, {
        vacation_hours: parseFloat(editBal.vacation_hours) || 0,
        sick_hours: parseFloat(editBal.sick_hours) || 0,
      }, { headers });
      toast({ title: 'Balance updated' });
      setEditBal(null);
      load();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || '', variant: 'destructive' }); }
  };

  const myBal = balances.find((b) => b.user_id === user?.id) || balances[0] || {};
  const pending = requests.filter((r) => r.status === 'pending');

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="time-off-page">
      <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Plane className="w-6 h-6 text-[#1E88E5]" /> Time Off</h1>

      {/* My balance + request form */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="ht-card p-5 rounded-xl border border-[#2D3748] bg-[#111C2B]">
          <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">My balances</p>
          <div className="flex gap-6" data-testid="my-pto-balance">
            <div><p className="text-3xl font-bold text-[#00C896]">{(myBal.vacation_hours ?? 0).toFixed(1)}h</p><p className="text-xs text-slate-400">Vacation</p></div>
            <div><p className="text-3xl font-bold text-[#FFA000]">{(myBal.sick_hours ?? 0).toFixed(1)}h</p><p className="text-xs text-slate-400">Sick</p></div>
          </div>
        </div>
        <div className="ht-card p-5 rounded-xl border border-[#2D3748] bg-[#111C2B] space-y-3">
          <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">Request time off</p>
          <div className="grid grid-cols-2 gap-3">
            <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className={inputCls} data-testid="pto-type-select">
              <option value="vacation">Vacation</option><option value="sick">Sick</option><option value="unpaid">Unpaid</option>
            </select>
            <Input type="number" min="0.5" step="0.5" placeholder="Hours (default 8/day)" value={form.total_hours}
              onChange={(e) => setForm((f) => ({ ...f, total_hours: e.target.value }))} data-testid="pto-hours-input" />
            <Input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} data-testid="pto-start-input" />
            <Input type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} data-testid="pto-end-input" />
          </div>
          <Input placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="pto-notes-input" />
          <Button onClick={submit} disabled={busy} className="w-full bg-[#1E88E5] hover:bg-blue-700 text-white" data-testid="pto-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Request'}
          </Button>
        </div>
      </div>

      {/* Admin: pending approvals */}
      {manager && pending.length > 0 && (
        <div className="ht-card p-5 rounded-xl border border-amber-600/40 bg-[#111C2B]">
          <p className="text-xs uppercase tracking-wider font-semibold text-amber-400 mb-3">Pending approval ({pending.length})</p>
          <div className="space-y-2" data-testid="pto-pending-list">
            {pending.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg border border-[#2D3748]">
                <div className="text-sm text-gray-100">
                  <b>{r.user_name}</b> · {r.type} · {r.start_date} → {r.end_date} · {r.total_hours}h
                  {r.notes && <span className="text-slate-400"> — {r.notes}</span>}
                </div>
                {isAdmin && (
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-[#00C896] hover:bg-emerald-600 text-white h-8" onClick={() => decide(r.id, 'approve')} data-testid={`pto-approve-${r.id}`}><Check className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" className="text-red-400 border-red-500/40 h-8" onClick={() => decide(r.id, 'deny')} data-testid={`pto-deny-${r.id}`}><X className="w-4 h-4" /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manager: company-wide time-off calendar (Jun 2026) */}
      {manager && <PtoCalendar requests={requests} />}

      {/* Requests history */}
      <div className="ht-card p-5 rounded-xl border border-[#2D3748] bg-[#111C2B]">
        <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">{manager ? 'All requests' : 'My requests'}</p>
        {requests.length === 0 ? <p className="text-sm text-slate-500">No requests yet.</p> : (
          <div className="space-y-1" data-testid="pto-request-list">
            {requests.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-[#1F2D3F] text-sm">
                <span className="text-gray-200">{manager && <b>{r.user_name} · </b>}{r.type} · {r.start_date} → {r.end_date} · {r.total_hours}h</span>
                <span className="flex items-center gap-3">
                  <span className="font-semibold text-xs uppercase" style={{ color: STATUS_COLORS[r.status] || '#A0AEC0' }}>{r.status}</span>
                  {r.status === 'pending' && r.user_id === user?.id && (
                    <button className="text-xs text-red-400 hover:underline" onClick={() => cancel(r.id)} data-testid={`pto-cancel-${r.id}`}>Cancel</button>
                  )}
                  {r.status === 'approved' && manager && (
                    <button
                      className="text-xs text-amber-400 hover:underline"
                      onClick={() => { if (window.confirm(`Cancel this approved ${r.type} time off and restore ${r.total_hours}h to ${r.user_name}?`)) decide(r.id, 'cancel'); }}
                      data-testid={`pto-admin-cancel-${r.id}`}
                    >Cancel &amp; restore</button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Admin: balances table */}
      {manager && (
        <div className="ht-card p-5 rounded-xl border border-[#2D3748] bg-[#111C2B]">
          <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">Worker balances</p>
          <div className="space-y-1" data-testid="pto-balances-table">
            {balances.map((b) => (
              <div key={b.user_id} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-[#1F2D3F] text-sm">
                <span className="text-gray-200">{b.name} <span className="text-slate-500 text-xs">({b.role})</span></span>
                {editBal?.user_id === b.user_id ? (
                  <span className="flex items-center gap-2">
                    <Input className="w-24 h-8" type="number" value={editBal.vacation_hours} onChange={(e) => setEditBal((x) => ({ ...x, vacation_hours: e.target.value }))} data-testid="pto-bal-vac-input" />
                    <Input className="w-24 h-8" type="number" value={editBal.sick_hours} onChange={(e) => setEditBal((x) => ({ ...x, sick_hours: e.target.value }))} data-testid="pto-bal-sick-input" />
                    <Button size="sm" className="h-8 bg-[#1E88E5] text-white" onClick={saveBal} data-testid="pto-bal-save">Save</Button>
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditBal(null)}>✕</Button>
                  </span>
                ) : (
                  <span className="flex items-center gap-4">
                    <span className="text-[#00C896]">{(b.vacation_hours ?? 0).toFixed(1)}h vac</span>
                    <span className="text-[#FFA000]">{(b.sick_hours ?? 0).toFixed(1)}h sick</span>
                    {isAdmin && <button className="text-xs text-blue-400 hover:underline" onClick={() => setEditBal({ ...b })} data-testid={`pto-bal-edit-${b.user_id}`}>Edit</button>}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
